import React from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
  recordLabel?: string;
  className?: string;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalRecords,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  recordLabel = "records",
  className = "",
}) => {
  const safeTotal = totalPages || 1;
  const from = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalRecords);

  const navBtnClass =
    "p-1.5 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors";

  return (
    <div
      className={`flex flex-col gap-3 px-4 py-3 bg-white border-t border-gray-200 rounded-b-lg shadow-sm sm:flex-row sm:items-center sm:justify-between ${className}`}
    >
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <span>Showing</span>
        <span className="font-semibold text-gray-800">{from}</span>
        <span>–</span>
        <span className="font-semibold text-gray-800">{to}</span>
        <span>of</span>
        <span className="font-semibold text-gray-800">{totalRecords}</span>
        <span>{recordLabel}</span>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 text-sm">
          <label className="text-gray-600">Rows per page:</label>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="border border-gray-300 rounded-md px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#00A8A5] focus:border-transparent"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        {/* Page navigation */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => onPageChange(1)}
            disabled={currentPage <= 1}
            className={navBtnClass}
            title="First page"
          >
            <ChevronsLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage <= 1}
            className={navBtnClass}
            title="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <span className="px-3 py-1 text-sm font-medium text-gray-700">
            Page{" "}
            <input
              type="number"
              min={1}
              max={safeTotal}
              value={currentPage}
              onChange={(e) => {
                const val = Number(e.target.value);
                if (val >= 1 && val <= safeTotal) {
                  onPageChange(val);
                }
              }}
              className="w-12 text-center border border-gray-300 rounded-md mx-1 py-0.5 focus:outline-none focus:ring-2 focus:ring-[#00A8A5]"
            />{" "}
            of {safeTotal}
          </span>

          <button
            onClick={() =>
              onPageChange(Math.min(safeTotal, currentPage + 1))
            }
            disabled={currentPage >= safeTotal}
            className={navBtnClass}
            title="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => onPageChange(safeTotal)}
            disabled={currentPage >= safeTotal}
            className={navBtnClass}
            title="Last page"
          >
            <ChevronsRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Pagination;

